import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/cn.ts';
import { IconButton } from '../icon-button/icon-button.tsx';
import { pageNumbers } from './pageNumbers.ts';
import type { DataTablePagination } from './types.ts';

const pageButton = [
  'min-w-7 cursor-pointer rounded-sm px-1.5 py-1 text-xs font-bold transition-colors',
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-faint',
].join(' ');

export interface TablePaginationProps extends DataTablePagination {
  onPageChange?: (page: number) => void;
}

/**
 * Where the reader is in the list, and the ways out of it.
 *
 * The count line stays even when there is one page: "1–8 of 8" is the answer to
 * "is this all of them", which a table without it cannot give.
 */
export function TablePagination({
  page,
  pageSize,
  total,
  onPageChange,
}: TablePaginationProps) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  function goTo(next: number) {
    if (next >= 1 && next <= totalPages && next !== page) {
      onPageChange?.(next);
    }
  }

  return (
    <nav
      data-slot='data-table-pagination'
      aria-label='Pagination'
      className='flex flex-wrap items-center justify-between gap-2 border-t border-line px-3.5 py-2.5'
    >
      <p className='text-xs font-semibold text-ink-muted'>
        {from}–{to} of {total}
      </p>
      {totalPages > 1 && (
        <div className='flex items-center gap-1'>
          <IconButton
            aria-label='Previous page'
            size='sm'
            disabled={page <= 1}
            onClick={() => goTo(page - 1)}
          >
            <ChevronLeft className='size-3.5' aria-hidden='true' />
          </IconButton>
          {pageNumbers(page, totalPages).map((entry, index) =>
            entry === 'ellipsis' ? (
              <span
                /* Two gaps can sit in one row; only the position tells them apart. */
                key={`ellipsis-${index}`}
                aria-hidden='true'
                className='px-1 text-xs font-bold text-ink-muted'
              >
                …
              </span>
            ) : (
              <button
                key={entry}
                type='button'
                aria-label={`Page ${entry}`}
                aria-current={entry === page ? 'page' : undefined}
                onClick={() => goTo(entry)}
                className={cn(
                  pageButton,
                  entry === page
                    ? 'bg-primary text-white ring-1 ring-selected-edge'
                    : 'text-ink-secondary hover:bg-fill',
                )}
              >
                {entry}
              </button>
            ),
          )}
          <IconButton
            aria-label='Next page'
            size='sm'
            disabled={page >= totalPages}
            onClick={() => goTo(page + 1)}
          >
            <ChevronRight className='size-3.5' aria-hidden='true' />
          </IconButton>
        </div>
      )}
    </nav>
  );
}
